<template>
  <section
    :class="['faq-accordion', { 'is-flow': flow }]"
    :style="boxStyle"
    aria-labelledby="faq-title"
  >
    <div class="faq-accordion__inner">
      <h2 id="faq-title" class="faq-accordion__title">{{ intro.title }}</h2>
      <p class="faq-accordion__subtitle">{{ intro.subtitle }}</p>
      <p class="faq-accordion__description">{{ intro.description }}</p>

      <div class="faq-accordion__list">
        <article
          v-for="(item, index) in items"
          :key="item.question"
          :class="['faq-accordion__item', { 'is-open': index === activeIndex }]"
        >
          <button
            class="faq-accordion__question"
            type="button"
            :aria-expanded="index === activeIndex"
            :aria-controls="`faq-answer-${index}`"
            @click="toggleItem(index)"
          >
            <span>{{ item.question }}</span>
            <span class="faq-accordion__icon" aria-hidden="true" />
          </button>

          <div
            :id="`faq-answer-${index}`"
            class="faq-accordion__answer"
            :aria-hidden="index !== activeIndex"
          >
            <div class="faq-accordion__answer-inner">
              <p class="faq-accordion__answer-body">{{ item.answer }}</p>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { CSSProperties } from 'vue'

import type { DesignBox, FaqItem } from '../../data/home2026'

const props = defineProps<{
  box: DesignBox
  intro: {
    title: string
    subtitle: string
    description: string
  }
  items: FaqItem[]
  scaledPx: (value: number) => string
  flow?: boolean
}>()

const activeIndex = ref(0)

const boxStyle = computed<CSSProperties>(() => ({
  ...(props.flow
    ? {
        width: '100%',
      }
    : {
        left: props.scaledPx(props.box.x),
        top: props.scaledPx(props.box.y),
        width: props.scaledPx(props.box.w),
        height: props.scaledPx(props.box.h),
      }),
}))

const toggleItem = (index: number) => {
  activeIndex.value = activeIndex.value === index ? -1 : index
}
</script>

<style scoped>
.faq-accordion {
  position: absolute;
  z-index: 3;
  box-sizing: border-box;
  overflow: hidden;
  background: #000;
  color: #fff;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.faq-accordion.is-flow {
  position: relative;
  left: auto;
  top: auto;
  height: auto;
  min-height: 0;
  padding-top: var(--faq-padding-top);
  padding-bottom: var(--faq-list-top);
  overflow: visible;
}

.faq-accordion__inner {
  width: min(var(--faq-content-width), calc(100% - var(--faq-side-padding) * 2));
  margin: 0 auto;
}

.faq-accordion__title,
.faq-accordion__subtitle,
.faq-accordion__description {
  margin: 0;
  text-align: center;
  letter-spacing: 0;
  -webkit-font-smoothing: antialiased;
}

.faq-accordion__title {
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--faq-title-font);
  font-weight: 700;
  line-height: var(--faq-title-line);
}

.faq-accordion__subtitle {
  margin-top: var(--faq-subtitle-top);
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--faq-subtitle-font);
  font-weight: 700;
  line-height: var(--faq-subtitle-line);
}

.faq-accordion__description {
  max-width: var(--faq-description-width);
  margin: var(--faq-description-top) auto 0;
  color: #fff;
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--faq-description-font);
  font-weight: 400;
  line-height: var(--faq-description-line);
}

.faq-accordion__list {
  margin-top: var(--faq-list-top);
}

.faq-accordion__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: var(--faq-item-bottom);
  text-align: center;
}

.faq-accordion__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: var(--faq-question-width);
  padding: 0 0 var(--faq-row-bottom);
  border: 0;
  border-bottom: var(--faq-border-width) solid #fff;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--faq-question-font);
  font-weight: 400;
  line-height: var(--faq-question-line);
  letter-spacing: 0;
  text-align: left;
  -webkit-font-smoothing: antialiased;
}

.faq-accordion__question span:first-child {
  max-width: calc(100% - var(--faq-icon-space));
}

.faq-accordion__icon {
  position: relative;
  flex: 0 0 var(--faq-icon-font);
  width: var(--faq-icon-font);
  height: var(--faq-icon-font);
}

.faq-accordion__icon::before,
.faq-accordion__icon::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 70%;
  height: var(--faq-icon-stroke, 2px);
  border-radius: 999px;
  background: #d9d9d9;
  content: '';
  transform: translate(-50%, -50%);
  transform-origin: center;
  transition: transform 180ms ease;
}

.faq-accordion__icon::after {
  transform: translate(-50%, -50%) rotate(90deg);
}

.faq-accordion__item.is-open .faq-accordion__icon::before {
  width: 80%;
  transform: translate(-50%, -50%) rotate(45deg);
}

.faq-accordion__item.is-open .faq-accordion__icon::after {
  width: 80%;
  transform: translate(-50%, -50%) rotate(-45deg);
}

.faq-accordion__answer {
  width: 100%;
  max-width: var(--faq-question-width);
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition:
    max-height 360ms cubic-bezier(0.19, 1, 0.22, 1),
    opacity 220ms ease;
}

.faq-accordion__item.is-open .faq-accordion__answer {
  max-height: 640px;
  opacity: 1;
}

.faq-accordion__answer-inner {
  min-height: 0;
  padding-top: var(--faq-answer-gap);
  overflow: hidden;
}

.faq-accordion__answer-body {
  margin: 0;
  padding: var(--faq-answer-padding);
  border-radius: var(--faq-answer-radius);
  background: #111;
  color: #ccc;
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--faq-answer-font);
  font-weight: 400;
  line-height: var(--faq-answer-line);
  letter-spacing: 0;
  text-align: left;
}

.faq-accordion__question:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

@media (prefers-reduced-motion: reduce) {
  .faq-accordion__answer {
    transition: none;
  }
}
</style>
